import { Switch } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { useEffect } from "react";
import colorPalette from "../../common/colors";
import defaultSize from "./constants/defaultSizes";

function ToggleButton(props) {
  const handleClick = (event) => {
    event.stopPropagation();
    props.onChange({
      field: props.name,
      value: event.target.checked,
      event: event,
    });
  };

  // When `size` (sm | md | lg | xl) is passed, render at that FIXED size.
  // When omitted, keep the original responsive-by-breakpoint sizing.
  const sizeConfig = props.size ? defaultSize[props.size] : null;
  const dimension = (key) =>
    sizeConfig
      ? `${sizeConfig[key]}px`
      : {
          xs: `${defaultSize.sm[key]}px`,
          sm: `${defaultSize.sm[key]}px`,
          md: `${defaultSize.md[key]}px`,
          lg: `${defaultSize.lg[key]}px`,
          lx: `${defaultSize.xl[key]}px`,
        };
  const checkedTransform = sizeConfig
    ? `translateX(${sizeConfig.width - sizeConfig.height}px)`
    : {
        xs: `translateX(${defaultSize.sm.width - defaultSize.sm.height}px)`,
        sm: `translateX(${defaultSize.sm.width - defaultSize.sm.height}px)`,
        md: `translateX(${defaultSize.sm.width - defaultSize.sm.height}px)`,
        lg: `translateX(${defaultSize.lg.width - defaultSize.lg.height}px)`,
        lx: `translateX(${defaultSize.xl.width - defaultSize.xl.height}px)`,
      };

  return (
    <Switch
      onChange={handleClick}
      onClick={(e) => e.stopPropagation()}
      checked={props.isChecked === true}
      focusVisibleClassName=".Mui-focusVisible"
      disableRipple
      disabled={props.isDisabled}
      sx={(theme) => ({
        height: dimension("height"),
        width: dimension("width"),
        padding: 0,
        "& .MuiSwitch-switchBase": {
          padding: 0,
          transitionDuration: "300ms",
          "&.Mui-checked": {
            transform: checkedTransform,
            color: "#fff",
            "& + .MuiSwitch-track": {
              backgroundColor:
                colorPalette.color.secondary.primaryTintSecondary,
              opacity: 1,
              border: 0, //`.6px solid ${colorPalette.sematics.lightBg.success}`
            },
            "& .MuiSwitch-thumb": {
              backgroundColor: colorPalette.color.primary.primary,
              border: 0,
              boxShadow: "-2px 0px 3px #838ACD",
              backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="5" width="6" viewBox="0 0 5 6"><line x1="0.5" y1="0.5" x2="0.5" y2="5.5" stroke="${encodeURIComponent(
                "#09471C"
              )}" strokeLinecap="round"/><line x1="2.5" y1="0.5" x2="2.5" y2="5.5" stroke="${encodeURIComponent(
                "#09471C"
              )}" strokeLinecap="round"/><line x1="4.5" y1="0.5" x2="4.5" y2="5.5" stroke="${encodeURIComponent(
                "#09471C"
              )}" strokeLinecap="round"/></svg>')`,
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.5,
            },
          },
          // '&.Mui-focusVisible .MuiSwitch-thumb': {     color: '#33cf4d',     border:
          // '2px solid #fff', },
          "&.Mui-disabled .MuiSwitch-thumb": {
            backgroundColor: theme.palette.grey[500],
          },
          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.7,
            backgroundColor: theme.palette.neutral["020"],
          },
        },
        "& .MuiSwitch-thumb": {
          boxSizing: "border-box",
          height: dimension("height"),
          width: dimension("height"),
          boxShadow: "2px 0px 3px #0000001A",
          border: `1px solid ${colorPalette.neutrals.lightBg.quinary}`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="5" width="6" viewBox="0 0 5 6"><line x1="0.5" y1="0.5" x2="0.5" y2="5.5" stroke="${encodeURIComponent(
            colorPalette.neutrals.lightBg.quinary
          )}" strokeLinecap="round"/><line x1="2.5" y1="0.5" x2="2.5" y2="5.5" stroke="${encodeURIComponent(
            colorPalette.neutrals.lightBg.quinary
          )}" strokeLinecap="round"/><line x1="4.5" y1="0.5" x2="4.5" y2="5.5" stroke="${encodeURIComponent(
            colorPalette.neutrals.lightBg.quinary
          )}" strokeLinecap="round"/></svg>')`,
        },
        "& .MuiSwitch-track": {
          border: `.6px solid ${colorPalette.neutrals.lightBg.quinary}`,
          borderRadius: 26 / 2,
          backgroundColor: "#D9D9D9",
          opacity: 1,
          transition: theme.transitions.create(["background-color"], {
            duration: 500,
          }),
        },
      })}
      size="small"
    />
  );
}

ToggleButton.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  isChecked: PropTypes.bool,
  isDisabled: PropTypes.bool,
  // Fixed size override. Omit to keep the responsive-by-breakpoint sizing.
  size: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
};

ToggleButton.defaultProps = {
  name: "",
  isChecked: false,
  onChange: () => {},
  isDisabled: false,
};

export default ToggleButton;
